import React, { useEffect, useState } from 'react';
import { Modal } from '@ofself/shell';
import { Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/common';
import Inspector, { Band, Facts, Note, Tags } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import SharedWithRow, { reachBreakdown, reachKinds } from '../../components/SharedWithRow';
import FilePreviewSection from '../../components/FilePreviewSection';
import AudioBlobPlayer from '../../components/AudioBlobPlayer';
import { usePlace } from '../../nucleus/place';
import { useSpaceAccess, writerOf } from '../../nucleus/access';
import {
    schemaOf, titleOf, textOf, isSealed, isFile, isRatified, isTheirs, ownerOf, daysUntil, unrelate,
} from '../nucleus/GraphModel';
import { NodeHistory, JsonSheet } from './NodeSheets';
import { readable, relationsOf, sharedWith, downloadNode, words } from './graphData';
import { ago, whenOf, plural } from '../../nucleus/format';

// who put it in the trash: the app, else the person ("You" for the viewer)
export function trasherOf(node, userId, inGroup) {
    if (node.trashed_by_app_name) return node.trashed_by_app_name;
    const u = node.trashed_by_user;
    const id = u?.id || node.trashed_by_user_id;
    if (id && userId && String(id) === String(userId)) return 'You';
    if (u?.display_name || u?.username) return u.display_name || u.username;
    return inGroup ? 'A member' : 'You';
}

const bytes = (n) => (!n ? null : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`);

// ONE NODE — on the Nodes page and in the Trash. Its standing, every field
// (opened where your key allows), its file, who can see it, what it links to,
// and its history. Its acts come in from the page (one array, shared with the
// right-click); reading — the JSON, the history, a file's preview — is a link
// in its band's head.
export default function NodeInspector({ node, group, tick, actions, autoPurge = false, onOpen, onClose, onChanged }) {
    const { encKeys, user } = useAuth();
    const { spaceName, group: space, to, navigate } = usePlace();
    const toast = useToast();
    const access = useSpaceAccess();
    const userId = user?.id || user?.user_id;
    const [fields, setFields] = useState(node.value_json || {});
    const [relations, setRelations] = useState(null);
    const [reach, setReach] = useState(null);
    const [modal, setModal] = useState(null);           // json | history | preview | share
    const [shareTick, setShareTick] = useState(0);
    const trashed = !!node.deleted_at;
    const theirs = isTheirs(node, group, userId);
    const own = !theirs && !trashed;
    const vj = node.value_json || {};
    const schema = schemaOf(node);

    useEffect(() => {
        let live = true;
        setFields(node.value_json || {});
        readable(node, encKeys, group).then((v) => { if (live) setFields(v || {}); }).catch(() => {});
        return () => { live = false; };
    }, [node, encKeys, group]);
    useEffect(() => {
        if (trashed) return undefined;
        let live = true;
        relationsOf(node, group).then((r) => { if (live) setRelations(r); }).catch(() => { if (live) setRelations([]); });
        return () => { live = false; };
    }, [node, group, trashed, tick]);
    // who can see it: personal and yours only (/nodes/<id>/shared-with answers only the owner)
    useEffect(() => {
        if (group || !own) return undefined;
        let live = true;
        sharedWith(node.id).then((r) => { if (live) setReach(r); }).catch(() => { if (live) setReach(null); });
        return () => { live = false; };
    }, [node.id, group, own, tick, shareTick]);

    const keys = Object.keys(fields || {}).sort().filter((k) => textOf(fields[k]) !== '');
    const purgeIn = trashed && autoPurge ? daysUntil(node.purge_after) : null;
    const merged = Array.isArray(vj.composed_from) && vj.composed_from.length > 0;
    const file = isFile(node);
    const download = async () => {
        try { await downloadNode(group, node); } catch (e) { toast.error?.(e?.message || 'Couldn’t download it.'); }
    };
    // a link is a node: removing one is a delete (who wrote the link is not read here)
    const removeLink = (r) => ({
        id: 'unrelate', label: 'Remove link', danger: true,
        ...(access.canOn('delete', r) ? {} : { disabled: true, hint: access.hint }),
        confirm: {
            title: `Remove “${words(r.type)}” ${r.outgoing ? 'to' : 'from'} “${r.otherTitle}”?`,
            body: 'The link goes to the trash, where you can restore it. Both nodes stay as they are.',
            label: 'Remove link',
        },
        run: async () => {
            await unrelate(group, r.id);
            setRelations((rs) => (rs || []).filter((x) => x.id !== r.id));
            toast.success?.('The link is in the trash.');
            onChanged?.();
        },
    });
    const writer = writerOf(node, userId, !!group, space);
    const stratum = isRatified(node) ? 'Ratified' : node.stratum ? words(node.stratum).replace(/^./, (c) => c.toUpperCase()) : null;

    return (
        <Inspector
            tag={[schema, trashed ? 'in the trash' : stratum, theirs ? 'shared with you' : null].filter(Boolean).join(' · ')}
            title={titleOf(node)}
            sub={trashed
                ? `Trashed ${ago(node.deleted_at, { dateAfter: 7 })}${node.trashed_by_app_name ? ` by ${node.trashed_by_app_name}` : ''}`
                : [writer && (group || node.created_by_app_name) ? `Written by ${writer === 'You' ? 'you' : writer}` : null, `changed ${ago(node.updated_at, { dateAfter: 7 })}`].filter(Boolean).join(', ')}
            actions={actions}
            onClose={onClose}
        >
            <Band title={trashed ? 'In the trash' : spaceName}>
                <Facts items={trashed ? [
                    { label: 'Trashed', value: ago(node.deleted_at, { dateAfter: 7 }) },
                    { label: 'By', value: trasherOf(node, userId, !!group) },
                    { label: 'Links', value: node.restorable_edge_count > 0 ? `${node.restorable_edge_count} come back with it` : 'None to bring back' },
                    { label: 'Purged', value: purgeIn != null ? `in ${plural(purgeIn, 'day')}` : 'Never by itself', tone: purgeIn != null && purgeIn < 3 ? 'live' : undefined },
                ] : [
                    { label: 'Schema', value: schema },
                    { label: 'Stratum', value: stratum || '—' },
                    { label: 'Owner', value: theirs ? (ownerOf(node) || 'Someone else') : group ? spaceName : 'You' },
                    { label: 'Written by', value: writer || 'A member' },
                ]} />
                {node.schema_id && (
                    <Tags items={[{ key: 'kind', label: `The ${schema} schema`, onPress: () => navigate(to('graph', 'schemas', `open=${node.schema_id}`)) }]} />
                )}
            </Band>

            <Band title={plural(keys.length, 'field')} aside={<button type="button" className="nx-link" onClick={() => setModal('json')}>JSON</button>}>
                {keys.length === 0 && <Note>No fields.</Note>}
                {keys.length > 0 && (
                    <dl className="graph-s-fields">
                        {keys.map((k) => (
                            <div key={k} className="graph-s-field">
                                <dt>{words(k)}</dt>
                                {isSealed(fields[k])
                                    ? <dd className="is-sealed"><Lock size={11} strokeWidth={2.2} aria-hidden="true" /> Encrypted. Your key doesn’t open it here.</dd>
                                    : <dd>{textOf(fields[k])}</dd>}
                            </div>
                        ))}
                    </dl>
                )}
            </Band>

            {file && (
                <Band title="File" aside={trashed ? null : (
                    <span className="graph-s-links">
                        <button type="button" className="nx-link" onClick={() => setModal('preview')}>Preview</button>
                        <button type="button" className="nx-link" onClick={download}>Download</button>
                    </span>
                )}>
                    <Facts items={[
                        { label: 'Name', value: vj.filename || titleOf(node) },
                        { label: 'Type', value: String(vj.mime_type || 'file').split(';')[0] },
                        bytes(vj.size_bytes) && { label: 'Size', value: bytes(vj.size_bytes) },
                    ]} />
                </Band>
            )}

            {vj.audio_raw_file_node_id && (
                <Band title="Recording">
                    <AudioBlobPlayer rawFileNodeId={vj.audio_raw_file_node_id} />
                </Band>
            )}

            {!group && own && (
                <Band title="Who can see it" aside={<button type="button" className="nx-link" onClick={() => setModal('share')}>Share</button>}>
                    {reach && reach.groups.length === 0 && <Note>Only you.</Note>}
                    {(reach?.groups || []).map((g) => (
                        <Row
                            key={g.realm_id || 'direct'}
                            name={g.realm_name || 'Shared directly'}
                            sub={g.is_public ? 'Anyone can reach it' : reachBreakdown(g, `${words(schema)}`)}
                            end={g.reaches?.length ? reachKinds(g) : undefined}
                            onPress={g.realm_id ? () => navigate(to('realms', null, `open=${g.realm_id}`)) : undefined}
                        />
                    ))}
                </Band>
            )}

            {!trashed && relations && relations.length > 0 && (
                <Band title="Linked" aside={relations.length}>
                    {relations.map((r) => (
                        <Row
                            key={r.id}
                            name={r.otherTitle}
                            sub={`${r.outgoing ? '→' : '←'} ${words(r.type)}`}
                            onPress={() => onOpen?.(r.other)}
                            actions={[
                                // the link is a node of its own: it opens like any other
                                { id: 'open-link', label: 'Open the link', run: () => onOpen?.(r.id) },
                                own && removeLink(r),
                            ].filter(Boolean)}
                        />
                    ))}
                    {own && <Note>Each link is a node of its own: right-click one to open it or remove it.</Note>}
                </Band>
            )}

            {merged && (
                <Band title="Merged from" aside={vj.composed_from.length}>
                    <Note>{vj.merge_reason || 'This node was made by merging others.'}</Note>
                    {vj.composed_from.map((id, i) => (
                        <Row key={`${id}-${i}`} name={vj.composed_from_titles?.[i] || `Node ${i + 1}`} onPress={() => onOpen?.(id)} />
                    ))}
                </Band>
            )}
            {vj.merged_into && (
                <Band title="Merged into">
                    <Row name={vj.merged_into_title || 'Another node'} sub="It lives on there" onPress={() => onOpen?.(vj.merged_into)} />
                </Band>
            )}

            {/* history is the owner's: /nodes/<id>/history answers 404 on a node shared with you */}
            <Band title="About" aside={trashed || theirs ? null : <button type="button" className="nx-link" onClick={() => setModal('history')}>History</button>}>
                <Facts items={[
                    { label: 'Created', value: whenOf(node.created_at, { year: true }) || '—' },
                    { label: 'Changed', value: whenOf(node.updated_at, { year: true }) || '—' },
                ]} />
                <Note><span className="graph-s-id">{node.id}</span></Note>
            </Band>

            {modal === 'json' && <JsonSheet title={`${titleOf(node)} · JSON`} value={node.value_json || {}} onClose={() => setModal(null)} />}
            {modal === 'history' && (
                <NodeHistory group={group} node={node} canRevert={own && access.can('edit') && access.canOn('edit', node)} revertHint={own ? access.hint : undefined} onReverted={() => onChanged?.()} onClose={() => setModal(null)} />
            )}
            {modal === 'preview' && (
                <Modal title={titleOf(node)} onClose={() => setModal(null)} size="tall">
                    <div className="graph-sheet graph-s-preview"><FilePreviewSection node={node} /></div>
                </Modal>
            )}
            {modal === 'share' && (
                <Modal title={`Share “${titleOf(node)}”`} lead="Who can see it, and handing it to someone." onClose={() => { setModal(null); setShareTick((t) => t + 1); }}>
                    <div className="graph-sheet graph-s-share"><SharedWithRow node={node} /></div>
                </Modal>
            )}
        </Inspector>
    );
}
